import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterInformationTitle from "../components/RegisterInformationTitle";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import InformationSubtitleList from "./InformationSubtitleList";

type DataIndex = keyof Types.InformationTitleDto;

function InformationTitleList() {
    const alert = useAlert();

    const [informationTitles, setInformationTitles] = useState<Array<Types.InformationTitleDto>>();
    const [currentInformationTitle, setcurrentInformationTitle] = useState<Types.InformationTitleDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteInformationTitle, setOpenDeleteInformationTitle] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.InformationTitleDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.InformationTitleDto> = [
        {
            align: "center",
            title: String.informationTitleId,
            dataIndex: "informationTitleId",
            key: "informationTitleId",
            ...getColumnSearchProps("informationTitleId"),
        },
        {
            align: "center",
            title: String.informationTitle,
            dataIndex: "informationTitle",
            key: "informationTitle",
            ...getColumnSearchProps("informationTitle"),
        },
    ];

    const requestAxiosGetInformationTitles = async () => {
        const response = await utilAxiosWithAuth().get(Request.INFORMATION_TITLE_LIST_URL);
        // console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetInformationTitles,
        error: errorGetInformationTitles,
        data: resultGetInformationTitles,
        execute: requestGetInformationTitles,
    } = useAsyncAxios(requestAxiosGetInformationTitles);

    useEffect(() => {
        if (resultGetInformationTitles === null) return;
        // console.log("resultGetInformationTitles", resultGetInformationTitles);

        setInformationTitles(resultGetInformationTitles.informationTitles);
    }, [resultGetInformationTitles]);

    useEffect(() => {
        if (errorGetInformationTitles === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetInformationTitles.response.data.code, "Error", errorGetInformationTitles.response.data.message);
    }, [errorGetInformationTitles]);

    const requestAxiosRemoveInformationTitle = async (informationTitle: Types.InformationTitleDto) => {
        const response = await utilAxiosWithAuth().delete(Request.CAR_CATEGORY_URL + "/" + informationTitle?.informationTitleId);
        return response.data;
    };

    const {
        loading: loadingRemoveInformationTitle,
        error: errorRemoveInformationTitle,
        data: resultRemoveInformationTitle,
        execute: requestRemoveInformationTitles,
    } = useAsyncAxios(requestAxiosRemoveInformationTitle);

    useEffect(() => {
        if (resultRemoveInformationTitle === null) return;
        // console.log("resultRemoveInformationTitle", resultRemoveInformationTitle);

        alert.setAlert(AlertType.SUCCESS, "카테고리 삭제 성공", "카테고리 삭제를 완료하였습니다.");

        requestGetInformationTitles();
    }, [resultRemoveInformationTitle]);

    useEffect(() => {
        if (errorRemoveInformationTitle === null) return;

        alert.setAlert(errorRemoveInformationTitle.response.data.code, "Error", errorRemoveInformationTitle.response.data.message);
    }, [errorGetInformationTitles]);

    useEffect(() => {
        requestGetInformationTitles();
    }, []);

    const onRow = (value: Types.InformationTitleDto) => {
        setEditMode("none");
        setcurrentInformationTitle(value);
    };

    const handleDeleteInformationTitle = () => {
        if (currentInformationTitle !== undefined) setOpenDeleteInformationTitle(true);
    };

    const handleDeleteInformationTitleOk = () => {
        setOpenDeleteInformationTitle(false);
        requestRemoveInformationTitles(currentInformationTitle);
    };

    const handleDeleteInformationTitleCancel = () => {
        setOpenDeleteInformationTitle(false);
    };

    const onRegisterInformationTitle = () => {
        requestGetInformationTitles();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>주요 안내 주제</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteInformationTitle()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="informationTitleId" dataSource={informationTitles} onRow={onRow} />

            {editMode === "add" && <RegisterInformationTitle editMode={editMode} onRegisterInformationTitle={onRegisterInformationTitle} />}

            {editMode === "update" && currentInformationTitle !== undefined && (
                <RegisterInformationTitle
                    editMode={editMode}
                    currentInformationTitle={currentInformationTitle}
                    onRegisterInformationTitle={onRegisterInformationTitle}
                />
            )}

            {openDeleteInformationTitle && (
                <Modal open={openDeleteInformationTitle} onOk={handleDeleteInformationTitleOk} onCancel={handleDeleteInformationTitleCancel}>
                    {currentInformationTitle?.informationTitle} 삭제하시겠습니까?
                </Modal>
            )}

            {currentInformationTitle !== undefined && (
                <InformationSubtitleList informationTitleId={currentInformationTitle.informationTitleId.toString()} />
            )}
        </>
    );
}

export default InformationTitleList;
